import { Alert, Button, Portal, Snackbar, Stack, useTheme } from '@mui/material';
import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import type { Theme } from '@mui/material/styles';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AtlasErrorToast } from './AtlasErrorToast';
import { AtlasErrorActionToast } from './AtlasErrorActionToast';
import { AtlasSuccessToast } from './AtlasSuccessToast';
import { AtlasSuccessActionToast } from './AtlasSuccessActionToast';
import { AtlasTranslationStartedToast } from './AtlasTranslationStartedToast';
import { atlasSpacing, type AtlasLensTheme } from '../Form/atlasLensStyles';
import { ATLAS_LENS_COMPONENT_VARS } from '../Form/atlasLensTokens';

/** Brief success confirmation — auto-dismiss after 5s (matches Templates archive toast). */
export const CONTENT_AUTHORING_SUCCESS_TOAST_AUTO_HIDE_MS = 5000;

export function useContentAuthoringToastSnackbarSx() {
  const theme = useTheme() as Theme & LensTheme;
  const atlasTheme = theme as AtlasLensTheme;
  const { tokens } = theme;

  const toastMargin =
    tokens?.component?.toast?.container?.margin?.value ??
    ATLAS_LENS_COMPONENT_VARS.toastContainerMargin;
  const toastMarginXs = atlasSpacing(atlasTheme, '2');

  return useMemo(
    () => ({
      // Portal to document.body — must sit above global header (modal + 5) and side sheets (modal).
      zIndex: (muiTheme: { zIndex: { modal: number } }) => muiTheme.zIndex.modal + 15,
      top: {
        xs: `calc(var(--atlas-global-header-height, 64px) + ${toastMarginXs})`,
        sm: `calc(var(--atlas-global-header-height, 64px) + ${toastMargin})`,
      },
      right: {
        xs: toastMarginXs,
        sm: toastMargin,
      },
    }),
    [toastMargin, toastMarginXs],
  );
}

export interface ContentAuthoringSuccessToastProps {
  open: boolean;
  message: string;
  onDismiss: () => void;
  dismissLabel?: string;
  actionLabel?: string;
  onAction?: () => void;
}

export function ContentAuthoringSuccessToast({
  open,
  message,
  onDismiss,
  dismissLabel,
  actionLabel,
  onAction,
}: ContentAuthoringSuccessToastProps) {
  const snackbarSx = useContentAuthoringToastSnackbarSx();

  return (
    <Portal>
      <Snackbar
        open={open}
        autoHideDuration={onAction ? null : CONTENT_AUTHORING_SUCCESS_TOAST_AUTO_HIDE_MS}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          ...snackbarSx,
          '& .MuiSnackbarContent-root': {
            p: 0,
            minWidth: 'unset',
            maxWidth: 'none',
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}
        slotProps={{
          content: {
            sx: {
              p: 0,
              minWidth: 'unset',
              maxWidth: 'none',
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
          },
        }}
        onClose={(_, reason) => {
          if (reason === 'clickaway' || (onAction && reason === 'timeout')) {
            return;
          }
          onDismiss();
        }}
      >
        {actionLabel && onAction ? (
          <AtlasSuccessActionToast
            message={message}
            dismissLabel={dismissLabel ?? 'Dismiss'}
            onDismiss={onDismiss}
            actionLabel={actionLabel}
            onAction={onAction}
          />
        ) : (
          <AtlasSuccessToast message={message} onDismiss={onDismiss} />
        )}
      </Snackbar>
    </Portal>
  );
}

export interface ContentAuthoringTranslationStartedToastProps {
  open: boolean;
  message: string;
  onDismiss: () => void;
}

/** Cyan-strip toast for translation started (add or retranslate). */
export function ContentAuthoringTranslationStartedToast({
  open,
  message,
  onDismiss,
}: ContentAuthoringTranslationStartedToastProps) {
  const snackbarSx = useContentAuthoringToastSnackbarSx();

  return (
    <Portal>
      <Snackbar
        open={open}
        autoHideDuration={CONTENT_AUTHORING_SUCCESS_TOAST_AUTO_HIDE_MS}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          ...snackbarSx,
          '& .MuiSnackbarContent-root': {
            p: 0,
            minWidth: 'unset',
            maxWidth: 'none',
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}
        slotProps={{
          content: {
            sx: {
              p: 0,
              minWidth: 'unset',
              maxWidth: 'none',
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
          },
        }}
        onClose={(_, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          onDismiss();
        }}
      >
        <AtlasTranslationStartedToast message={message} onDismiss={onDismiss} />
      </Snackbar>
    </Portal>
  );
}

export interface ContentAuthoringErrorToastProps {
  open: boolean;
  message: string;
  onDismiss: () => void;
  onRetry?: () => void;
  dismissLabel?: string;
  retryLabel?: string;
}

export function ContentAuthoringErrorToast({
  open,
  message,
  onDismiss,
  onRetry,
  dismissLabel,
  retryLabel,
}: ContentAuthoringErrorToastProps) {
  const snackbarSx = useContentAuthoringToastSnackbarSx();

  return (
    <Portal>
      <Snackbar
        open={open}
        autoHideDuration={null}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          ...snackbarSx,
          '& .MuiSnackbarContent-root': {
            p: 0,
            minWidth: 'unset',
            maxWidth: 'none',
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}
        slotProps={{
          content: {
            sx: {
              p: 0,
              minWidth: 'unset',
              maxWidth: 'none',
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
          },
        }}
        onClose={(_, reason) => {
          if (reason === 'clickaway' || reason === 'timeout') {
            return;
          }
          onDismiss();
        }}
      >
        {onRetry ? (
          <AtlasErrorActionToast
            message={message}
            dismissLabel={dismissLabel ?? 'Dismiss'}
            onDismiss={onDismiss}
            actionLabel={retryLabel ?? 'Try again'}
            onAction={onRetry}
          />
        ) : (
          <AtlasErrorToast message={message} onDismiss={onDismiss} />
        )}
      </Snackbar>
    </Portal>
  );
}

export interface ContentAuthoringTranslationOutcomeToastProps {
  open: boolean;
  severity: 'success' | 'warning' | 'error';
  message: string;
  onDismiss: () => void;
  onGoToTranslations?: () => void;
  dismissLabel?: string;
  goToTranslationsLabel?: string;
}

/** Translation job finished — shown on courses list with Go to translations + Dismiss (CEE-1048). */
export function ContentAuthoringTranslationOutcomeToast({
  open,
  severity,
  message,
  onDismiss,
  onGoToTranslations,
  dismissLabel,
  goToTranslationsLabel,
}: ContentAuthoringTranslationOutcomeToastProps) {
  const { t } = useTranslation();
  const snackbarSx = useContentAuthoringToastSnackbarSx();
  const resolvedDismissLabel =
    dismissLabel ??
    t('page.authoring_courses.translations.outcome_toast.dismiss', { defaultValue: 'Dismiss' });
  const resolvedGoToTranslationsLabel =
    goToTranslationsLabel ??
    t('page.authoring_courses.translations.outcome_toast.go_to_translations', {
      defaultValue: 'Go to translations',
    });

  return (
    <Portal>
      <Snackbar
        open={open}
        autoHideDuration={null}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={snackbarSx}
        onClose={(_, reason) => {
          if (reason === 'clickaway' || reason === 'timeout') {
            return;
          }
          onDismiss();
        }}
      >
        <Alert
          severity={severity}
          variant="divided"
          aria-live="polite"
          sx={{ minWidth: 300 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button variant="text" size="medium" onClick={onDismiss}>
                {resolvedDismissLabel}
              </Button>
              {onGoToTranslations ? (
                <Button variant="text" size="medium" onClick={onGoToTranslations}>
                  {resolvedGoToTranslationsLabel}
                </Button>
              ) : null}
            </Stack>
          }
        >
          {message}
        </Alert>
      </Snackbar>
    </Portal>
  );
}
